import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  fetchCourses,
  fetchEntity,
  fetchGroups,
  saveEntity,
  sendChatMessage,
  type ChatMessage,
  type EntityDetail,
  type GroupNode,
} from '../api/client.js';
import { buildCourseBrowserPath, parseGroupId } from '../lib/authorNavigation.js';
import { AiChatPane, type AiProposal } from './AiChatPane.js';
import { ManualEditPane } from './ManualEditPane.js';
import { PlayerPreview } from './PlayerPreview.js';

export function EntityEditor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { courseId = '', entityId = '' } = useParams();
  const numericEntityId = Number(entityId);

  const [originalCopy, setOriginalCopy] = useState<EntityDetail | null>(null);
  const [workingCopy, setWorkingCopy] = useState<EntityDetail | null>(null);
  const [baseRevisionHash, setBaseRevisionHash] = useState('');
  const [groups, setGroups] = useState<GroupNode[]>([]);
  /**
   * The title of the course this editor is open on. Read from the API catalog, never derived from
   * `courseId` — the id encodes nothing (`20b` is a Level I vintage, not Level II). A failure leaves
   * it null and the breadcrumb names the bare course key instead: less information, never wrong
   * information. Until 2026-08-28 this breadcrumb read "CFA Level I 2027" for every course, on the
   * one screen in the tool that saves straight to live content.
   */
  const [courseTitle, setCourseTitle] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [pendingProposal, setPendingProposal] = useState<AiProposal | null>(null);
  const [lastAppliedSnapshot, setLastAppliedSnapshot] = useState<EntityDetail | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [editMode, setEditMode] = useState<'preview' | 'manual'>('preview');
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [changeDescription, setChangeDescription] = useState('');
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const isDirty = useMemo(
    () => JSON.stringify(originalCopy) !== JSON.stringify(workingCopy),
    [originalCopy, workingCopy],
  );
  const activeGroupId = useMemo(
    () => parseGroupId(searchParams.get('groupId')) ?? workingCopy?.group_id ?? null,
    [searchParams, workingCopy],
  );
  const breadcrumb = useMemo(() => buildBreadcrumb(groups, activeGroupId), [activeGroupId, groups]);

  useEffect(() => {
    if (!courseId || !Number.isFinite(numericEntityId)) {
      setError('Invalid entity route');
      return;
    }

    let cancelled = false;
    setError('');

    void Promise.all([fetchEntity(courseId, numericEntityId), fetchGroups(courseId)])
      .then(([entity, nextGroups]) => {
        if (cancelled) {
          return;
        }
        setOriginalCopy(entity);
        setWorkingCopy(entity);
        setBaseRevisionHash(entity._revisionHash);
        setGroups(nextGroups);
        setChatHistory([]);
        setPendingProposal(null);
        setLastAppliedSnapshot(null);
        setCanUndo(false);
        setEditMode('preview');
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : 'Unable to load entity');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [courseId, numericEntityId]);

  useEffect(() => {
    let cancelled = false;
    void fetchCourses()
      .then((courses) => {
        if (!cancelled) setCourseTitle(courses.find((course) => course.id === courseId)?.title ?? null);
      })
      .catch(() => { if (!cancelled) setCourseTitle(null); });
    return () => { cancelled = true; };
  }, [courseId]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  function mutateWorkingCopy(mutator: (draft: EntityDetail) => EntityDetail) {
    setWorkingCopy((current) => (current ? mutator(current) : current));
    setCanUndo(false);
  }

  async function handleSendMessage(instruction: string) {
    if (!workingCopy || !courseId) {
      return;
    }

    const nextHistory = [...chatHistory, { role: 'user' as const, content: instruction }];
    setChatHistory(nextHistory);
    setPendingProposal(null);
    setCanUndo(false);
    setIsSending(true);

    try {
      const proposal = await sendChatMessage({
        courseId,
        entityId: numericEntityId,
        instruction,
        currentDocument: workingCopy,
        chatHistory: nextHistory,
      });
      setChatHistory((current) => [...current, { role: 'assistant', content: proposal.message }]);
      setPendingProposal({ ...proposal, status: 'pending' });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to generate AI proposal');
    } finally {
      setIsSending(false);
    }
  }

  function handleApplyProposal() {
    if (!pendingProposal) {
      return;
    }
    setLastAppliedSnapshot(workingCopy);
    setWorkingCopy(pendingProposal.document);
    setPendingProposal({ ...pendingProposal, status: 'applied' });
    setCanUndo(true);
  }

  function handleRejectProposal() {
    if (!pendingProposal) {
      return;
    }
    setPendingProposal({ ...pendingProposal, status: 'rejected' });
    setCanUndo(false);
  }

  function handleUndo() {
    if (!lastAppliedSnapshot || !canUndo) {
      return;
    }
    setWorkingCopy(lastAppliedSnapshot);
    setPendingProposal(null);
    setLastAppliedSnapshot(null);
    setCanUndo(false);
  }

  async function handleSave() {
    if (!workingCopy || !courseId || !changeDescription.trim()) {
      return;
    }
    setIsSaving(true);
    setSaveError('');

    try {
      const result = await saveEntity({
        courseId,
        entityId: numericEntityId,
        document: workingCopy,
        baseRevisionHash,
        changeDescription: changeDescription.trim(),
      });

      if (!result.success) {
        throw new Error(result.validationErrors?.join(', ') || 'Unable to save entity');
      }

      const refreshed = await fetchEntity(courseId, numericEntityId);
      setOriginalCopy(refreshed);
      setWorkingCopy(refreshed);
      setBaseRevisionHash(refreshed._revisionHash);
      setPendingProposal(null);
      setLastAppliedSnapshot(null);
      setCanUndo(false);
      setShowSaveDialog(false);
      setChangeDescription('');
    } catch (reason) {
      const conflict = (reason as Error & { conflict?: { conflict?: boolean } }).conflict;
      if (conflict?.conflict) {
        setSaveError('Someone else saved changes. Reload to see the latest version.');
      } else {
        setSaveError(reason instanceof Error ? reason.message : 'Unable to save entity');
      }
    } finally {
      setIsSaving(false);
    }
  }

  function requestNavigation(nextPath: string) {
    if (isDirty) {
      setPendingNavigationPath(nextPath);
      setShowLeaveDialog(true);
      return;
    }
    navigate(nextPath);
  }

  if (error) {
    return <div className="px-6 py-6 text-sm font-medium text-red-700">{error}</div>;
  }

  if (!workingCopy) {
    return <div className="px-6 py-6 text-sm text-slate-500">Loading entity editor…</div>;
  }

  const previewLayoutStyle = {
    '--author-ai-panel-width': 'clamp(19rem, 23vw, 23rem)',
    '--author-minimap-height': 'clamp(210px, 20vh, 320px)',
  } as CSSProperties;

  return (
    <div className="grid gap-5">
      <div className="sticky top-[76px] z-10 overflow-hidden rounded-[28px] border border-primary/8 bg-white/88 shadow-[0_20px_50px_rgba(22,38,31,0.08)] backdrop-blur-md">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <div className="text-sm text-slate-500 truncate">
              {/*
                The course this editor is actually open on — it used to say "CFA Level I 2027" for
                every course, on the one screen in the tool that can save straight to live content.
                A Level III lesson under a Level I breadcrumb is the wire-crossing the reviewer
                described, in its most expensive form. The course key is shown when no title has
                loaded: less information, never wrong information.
              */}
              <button
                className="transition hover:text-primary"
                onClick={() => requestNavigation(buildCourseBrowserPath(courseId))}
                type="button"
              >
                {courseTitle ?? `Course ${courseId}`}
              </button>
              {breadcrumb.map((group) => (
                <span key={group.id}>
                  {' '}
                  ›{' '}
                  <button
                    className="transition hover:text-primary"
                    onClick={() => requestNavigation(buildCourseBrowserPath(courseId, group.id))}
                    type="button"
                  >
                    {group.title}
                  </button>
                </span>
              ))}
              {' '}
              › <span className="font-semibold text-slate-900">{workingCopy.title}</span>
            </div>
            <span className="editor-badge">{workingCopy.item_type_slug ?? 'ITEM'}</span>
            <span className="editor-badge">{workingCopy.published ? 'Published' : 'Draft'}</span>
            {isDirty ? <span className="editor-badge bg-amber-100 text-amber-800">Unsaved</span> : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              className="grid size-9 place-items-center rounded-xl border border-primary/12 bg-white text-slate-600 transition hover:bg-primary/5"
              type="button"
            >
              <span className="text-base leading-none">⚙</span>
            </button>
            <button
              disabled={!isDirty || isSaving}
              onClick={() => setShowSaveDialog(true)}
              className={`rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-cream transition ${
                !isDirty || isSaving ? 'cursor-not-allowed opacity-50' : 'hover:bg-primary/90'
              }`}
              type="button"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              className="rounded-xl border border-primary/12 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-primary/5"
              onClick={() => requestNavigation(buildCourseBrowserPath(courseId, activeGroupId))}
              type="button"
            >
              Back
            </button>
          </div>
        </div>
      </div>

      {editMode === 'preview' ? (
        <div
          className="relative editor-panel overflow-hidden xl:h-[calc(100vh-13rem)]"
          style={previewLayoutStyle}
        >
          <div className="absolute inset-0">
            <PlayerPreview entity={workingCopy} />
          </div>
          <div
            className="absolute left-0 top-0 z-10 flex w-[var(--author-ai-panel-width)] flex-col overflow-hidden border-r border-primary/8 bg-white/97"
            style={{ bottom: 'var(--author-minimap-height, clamp(210px, 20vh, 320px))' }}
          >
            <AiChatPane
              embedded
              canUndo={canUndo}
              chatHistory={chatHistory}
              isSending={isSending}
              onApplyProposal={handleApplyProposal}
              onRejectProposal={handleRejectProposal}
              onSendMessage={handleSendMessage}
              onUndo={handleUndo}
              pendingProposal={pendingProposal}
              workingCopy={workingCopy}
            />
          </div>
        </div>
      ) : (
        <div className="grid gap-4 xl:h-[calc(100vh-13rem)] xl:grid-cols-[var(--author-ai-panel-width)_minmax(0,1fr)]" style={previewLayoutStyle}>
          <AiChatPane
            canUndo={canUndo}
            chatHistory={chatHistory}
            className="xl:min-h-0"
            isSending={isSending}
            onApplyProposal={handleApplyProposal}
            onRejectProposal={handleRejectProposal}
            onSendMessage={handleSendMessage}
            onUndo={handleUndo}
            pendingProposal={pendingProposal}
            workingCopy={workingCopy}
          />

          <section className="grid min-w-0 xl:h-full">
            <div className="editor-panel flex min-h-[520px] justify-center overflow-hidden p-4 sm:p-6 xl:h-full xl:min-h-0">
              <div className="relative h-full w-full overflow-hidden">
                <div className="absolute inset-0 overflow-auto">
                  <ManualEditPane
                    baseRevisionHash={baseRevisionHash}
                    isDirty={isDirty}
                    onPatch={mutateWorkingCopy}
                    onReset={() => {
                      setWorkingCopy(originalCopy);
                      setPendingProposal(null);
                      setCanUndo(false);
                    }}
                    workingCopy={workingCopy}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {showSaveDialog ? (
        <div className="fixed inset-0 z-20 grid place-items-center bg-slate-950/35 p-4">
          <div className="editor-panel grid w-full max-w-[520px] gap-4 bg-white px-6 py-6">
            <h2 className="font-serif text-2xl text-primary">Describe this change</h2>
            <textarea
              className="min-h-[140px] rounded-2xl border border-primary/12 bg-white px-4 py-3 text-sm leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary/30 focus:ring-0"
              onChange={(event) => setChangeDescription(event.target.value)}
              placeholder="Summarize what changed in 1-2 sentences"
              value={changeDescription}
            />
            {saveError ? (
              <div className="flex flex-wrap items-center gap-2 text-sm text-red-700">
                {saveError}
                {saveError.includes('Reload') ? (
                  <button className="font-medium text-sky-700 hover:text-sky-800" onClick={() => window.location.reload()} type="button">
                    Reload
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="flex justify-end gap-3">
              <button
                className="rounded-2xl border border-primary/12 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-primary/5"
                onClick={() => setShowSaveDialog(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                disabled={!changeDescription.trim() || isSaving}
                onClick={handleSave}
                className={`rounded-2xl bg-primary px-4 py-2.5 text-sm font-semibold text-cream transition ${
                  !changeDescription.trim() || isSaving ? 'cursor-not-allowed opacity-50' : 'hover:bg-primary/90'
                }`}
                type="button"
              >
                Confirm save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showLeaveDialog ? (
        <div className="fixed inset-0 z-20 grid place-items-center bg-slate-950/35 p-4">
          <div className="editor-panel grid w-full max-w-[520px] gap-4 bg-white px-6 py-6">
            <h2 className="font-serif text-2xl text-primary">Discard unsaved changes?</h2>
            <p className="text-sm leading-6 text-slate-600">
              You have unsaved changes. Leave this editor and discard them, or stay?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLeaveDialog(false);
                  setPendingNavigationPath(null);
                }}
                className="rounded-2xl border border-primary/12 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-primary/5"
                type="button"
              >
                Stay
              </button>
              <button
                onClick={() => {
                  setShowLeaveDialog(false);
                  if (pendingNavigationPath) {
                    navigate(pendingNavigationPath);
                  }
                  setPendingNavigationPath(null);
                }}
                className="rounded-2xl bg-primary px-4 py-2.5 text-sm font-semibold text-cream transition hover:bg-primary/90"
                type="button"
              >
                Discard and leave
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildBreadcrumb(groups: GroupNode[], groupId: number | null) {
  if (groupId == null) {
    return [];
  }
  const all = flattenGroups(groups);
  const byId = new Map(all.map((group) => [group.id, group]));
  const trail: GroupNode[] = [];
  let current = byId.get(groupId);
  while (current) {
    trail.unshift(current);
    current = current.parentId >= 0 ? byId.get(current.parentId) : undefined;
  }
  return trail;
}

function flattenGroups(groups: GroupNode[]): GroupNode[] {
  return groups.flatMap((group) => [group, ...flattenGroups(group.children)]);
}
